import { Table as ReactTable, Row, flexRender } from "@tanstack/react-table";
import {
  VirtualItem,
  Virtualizer,
  useVirtualizer,
} from "@tanstack/react-virtual";
import { cn } from "@theprelude/core";
import {
  Loading,
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollBar,
  ScrollCorner,
  TBody,
  TCell,
  THead,
  THeadRow,
  TRow,
  Table,
} from "@theprelude/ds";
import React, {
  ForwardedRef,
  forwardRef,
  Fragment,
  ReactElement,
  Ref,
  Suspense,
  UIEventHandler,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { AppErrorPage } from "@/components/app-shell/app-shell";
import { mergeRefs } from "@/lib/merge-refs";
import { TableFilterInput } from "./filter-input";
import { VirtualSortedHeader } from "./header";
import { getGridTemplateColumns } from "./hooks";
import { LoadingTable } from "./loading-table";
import { TableOptions } from "./table-options";

export interface VirtualTableProps<TData> {
  emptyState?: React.ReactNode;
  loading?: boolean;
  table: ReactTable<TData>;
  showFilter?: boolean;
  className?: string;
  height?: number;
  size?: "small" | "large";
  selectedRowClassName?: string;
  withActionBar?: boolean;
  hideOptions?: boolean;
  rowClassName?: string | ((row: Row<TData>) => string);
  onScroll?: UIEventHandler<HTMLDivElement>;
}

function VirtualTableInner<TData>(
  {
    showFilter,
    table,
    className,
    height,
    loading,
    emptyState,
    size = "small",
    selectedRowClassName,
    withActionBar = false,
    hideOptions = false,
    rowClassName,
    onScroll,
  }: VirtualTableProps<TData>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { rows } = table.getRowModel();
  const [containerWidth, setContainerWidth] = useState(0);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => (size === "small" ? 37 : 52),
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent?.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const gridTemplateColumns = getGridTemplateColumns(table);

  return (
    <>
      {showFilter && <TableFilterInput className="mb-4" table={table} />}
      <ScrollAreaRoot>
        {!hideOptions && <TableOptions table={table} />}
        <ScrollAreaViewport
          ref={mergeRefs(tableContainerRef, ref, (node) =>
            setContainerWidth(node?.clientWidth ?? 0),
          )}
          className={cn("relative w-full flex-1 overflow-auto", className)}
          onScroll={onScroll}
          style={height ? { height } : undefined}
          onResize={(event) => {
            if (event.target instanceof HTMLElement) {
              setContainerWidth(event.target.clientWidth);
            }
          }}
        >
          <Table>
            <THead>
              {table.getHeaderGroups().map((headerGroup) => (
                <THeadRow
                  key={headerGroup.id}
                  style={{
                    gridTemplateColumns,
                  }}
                  className="relative"
                >
                  {headerGroup.headers.map((header) => (
                    <VirtualSortedHeader key={header.id} header={header} />
                  ))}
                </THeadRow>
              ))}
            </THead>
            <VirtualTableBody
              table={table}
              containerWidth={containerWidth}
              loading={loading}
              emptyState={emptyState}
              rowVirtualizer={rowVirtualizer}
              size={size}
              selectedRowClassName={selectedRowClassName}
              rowClassName={rowClassName}
            />
          </Table>
          {withActionBar && table.getIsSomeRowsSelected() && (
            <div className="h-[55px]" />
          )}
        </ScrollAreaViewport>
        <ScrollBar
          orientation="vertical"
          className="z-10 border-l-0 border-r border-secondary-background bg-surface-background pt-[40px]"
        />
        <ScrollCorner />
        <ScrollBar
          orientation="horizontal"
          className="z-10 border border-r-0 border-t-0 border-secondary-background bg-surface-background"
        />
      </ScrollAreaRoot>
    </>
  );
}

function VirtualTableBody<TData>({
  table,
  containerWidth,
  loading,
  emptyState,
  rowVirtualizer,
  size = "small",
  selectedRowClassName,
  rowClassName,
}: {
  rowVirtualizer: Virtualizer<any, any>;
  table: ReactTable<TData>;
  containerWidth: number;
  loading?: boolean;
  emptyState?: React.ReactNode;
  size?: "small" | "large";
  selectedRowClassName?: string;
  rowClassName?: string | ((row: Row<TData>) => string);
}) {
  const rows = table.getRowModel().rows;

  if (loading) {
    return <LoadingTable table={table} rowCount={5} />;
  }

  if (rows.length === 0 && emptyState) {
    return (
      <TBody>
        <TRow>
          <TCell className="flex min-w-0 items-center justify-center text-sm text-secondary-foreground">
            {emptyState}
          </TCell>
        </TRow>
      </TBody>
    );
  }

  const gridTemplateColumns = getGridTemplateColumns(table);

  return (
    <Suspense fallback={<LoadingTable table={table} rowCount={5} />}>
      <TBody style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index] as Row<TData>;

          const dynamicRowClassName =
            typeof rowClassName === "function"
              ? rowClassName(row)
              : rowClassName;

          const translateFn = rowVirtualizer.options.horizontal
            ? "translateX"
            : "translateY";

          const measureElement = table.options.getRowCanExpand?.(row)
            ? createExpandableMeasureElement(rowVirtualizer)
            : rowVirtualizer.measureElement;

          return (
            <Fragment key={row.id}>
              <TRow
                className={cn(
                  "absolute",
                  size === "large" && "h-[52px]",
                  row.getIsSelected() && selectedRowClassName,
                )}
                data-index={virtualRow.index} //needed for dynamic row height measurement
                ref={measureElement} //measure dynamic row height
                style={{
                  gridTemplateColumns,
                  transform: `${translateFn}(${virtualRow.start}px)`,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <TCell
                      key={cell.id}
                      className={cell.column.accessorFn && dynamicRowClassName}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TCell>
                  );
                })}
              </TRow>
              {row.getIsExpanded() && (
                <ExpandableContentRow
                  containerWidth={containerWidth}
                  row={row}
                  rowVirtualizer={rowVirtualizer}
                  virtualRow={virtualRow}
                >
                  {table.options.meta?.getExpandableContent?.(row)}
                </ExpandableContentRow>
              )}
            </Fragment>
          );
        })}
      </TBody>
    </Suspense>
  );
}

const ExpandableContentRow = <TData,>({
  children,
  containerWidth,
  row,
  rowVirtualizer,
  virtualRow,
}: {
  children: React.ReactNode;
  containerWidth: number;
  row: Row<TData>;
  rowVirtualizer: Virtualizer<any, any>;
  virtualRow: VirtualItem;
}) => {
  const translateFn = rowVirtualizer.options.horizontal
    ? "translateX"
    : "translateY";
  const direction = rowVirtualizer.options.horizontal ? "width" : "height";

  return (
    <TRow
      ref={(node) => {
        if (node) {
          node.style.transform = `${translateFn}(${virtualRow.start + virtualRow.size - node.getBoundingClientRect()[direction]}px)`;
        }
      }}
      className="absolute h-auto border-t-secondary-background p-0"
      data-index={virtualRow.index}
      onAnimationIterationCapture={rowVirtualizer.measure}
    >
      <TCell colSpan={row.getAllCells().length} className="relative p-0">
        <div
          className="sticky left-0 px-4 py-2"
          style={{ width: `${containerWidth}px` }}
        >
          <ErrorBoundary FallbackComponent={AppErrorPage}>
            <Suspense fallback={<Loading className="my-2" />}>
              <RenderLoader onLoad={rowVirtualizer.measure}>
                {children}
              </RenderLoader>
            </Suspense>
          </ErrorBoundary>
        </div>
      </TCell>
    </TRow>
  );
};

const RenderLoader = ({
  children,
  onLoad,
}: {
  children: React.ReactNode;
  onLoad: () => void;
}) => {
  const onLoadRef = useRef(onLoad);

  useLayoutEffect(() => {
    onLoadRef.current();
  }, []);

  return children;
};

const createExpandableMeasureElement = <T extends Element>(
  rowVirtualizer: Virtualizer<T, any>,
) => {
  return (node: HTMLTableRowElement | null) => {
    if (!node) {
      return;
    }

    const index = rowVirtualizer.indexFromElement(node);
    const direction = rowVirtualizer.options.horizontal ? "width" : "height";
    const nodeSize = node.getBoundingClientRect()[direction];

    const expandableEl = node.nextElementSibling;
    const expandableIndex = expandableEl
      ? rowVirtualizer.indexFromElement(expandableEl)
      : -1;
    const expandableSize =
      expandableEl && expandableIndex === index
        ? expandableEl.getBoundingClientRect()[direction]
        : 0;

    rowVirtualizer.resizeItem(index, nodeSize + expandableSize);
  };
};

export const VirtualTable = forwardRef(VirtualTableInner) as <TData>(
  props: VirtualTableProps<TData> & { ref?: Ref<HTMLDivElement> },
) => ReactElement<TData>;
